import React, {useState, useCallback, useEffect} from 'react'

// Components
import {Form, FormField, FormInput, FormCheckbox} from '@/components/ui/form'
import {routeTypes, Button as PrimativeButton} from '@/components/ui/button'
import {Button} from '@/components/cta'
import {Alert, AlertDescription} from '@/components/ui/alert'
import GoogleSignIn from './googleSignIn'
import AccountErrorSummary from '../accountErrorSummary'
import {buttonFunctions} from '../../cta'

// Form dependencies and helpers
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'

import {
    AuthHelpers,
    useAuthHelper,
    useCustomerId,
    useCustomerType,
    useCustomerBaskets,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {usePrevious} from '@salesforce/retail-react-app/app/hooks/use-previous'
import {useAppOrigin} from '~/app/hooks/use-app-origin'
import {cn} from '@/lib/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isEditing} from '@builder.io/sdk-react'
import {useCurrentBasket} from '@/hooks/basket/useCurrentBasket'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import {mockCustomerData} from '../../../data/'
import {IBuilderPageReference} from '@salesforce/retail-react-app/hec/types/common'
import {TurnstileWidget} from '~/app/utils/cloudflare/turnstile/turnstile'
import {useClientSitePreferences} from 'hec/context/site-preferences'
import useUpdateProfile from 'hec/hooks/commerce-sdk/useUpdateProfile'

interface FormLabels {
    email?: string
    password?: string
    rememberMe?: string
    forgotPassword?: string
}

interface SignInFormProps {
    closeDrawer?: () => void
    onForgotPassword?: () => void
    enableGuestCheckout?: boolean
    checkoutPageReference?: IBuilderPageReference
    className?: string
    formLabels?: FormLabels
}

const SignInForm = ({
    closeDrawer = () => {},
    onForgotPassword = () => {},
    enableGuestCheckout,
    checkoutPageReference,
    className = '',
    formLabels = {},
    ...remainingProps
}: SignInFormProps) => {
    const [showAlert, setShowAlert] = useState(false)
    const customerId = useCustomerId()
    const isEdit = isEditing()
    const {
        data: customerData,
        isError: isCustomerError,
        error: customerError
    } = useCurrentCustomer()
    const customer = isEdit ? mockCustomerData : customerData
    const isLoggedIn = !customer.isGuest && customer.isRegistered
    const {customerType} = useCustomerType()
    const prevAuthType = usePrevious(customerType)
    const appOrigin: string = useAppOrigin()
    const basket = useCurrentBasket({enabled: true})
    const basketTotalItems = basket?.derivedData?.totalItems || 0
    const prefs = useClientSitePreferences()
    const turnstileEnabled = !isServer && prefs?.CloudFlareConfigs?.cloudFlareTurnstileEnabled
    const googleOAuthEnabled = prefs?.googleOAuthPreferences?.enableGoogleOAuth === true
    const [isButtonDisabled, setIsButtonDisabled] = useState(!!turnstileEnabled)

    // Update button state when turnstileEnabled changes
    useEffect(() => {
        setIsButtonDisabled(turnstileEnabled)
    }, [turnstileEnabled])

    const FormSchema = z.object({
        email: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .email({message: 'Please enter a valid email address'}),
        password: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .regex(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\da-zA-Z]).{8,}$/,
                'Password must contain 8 characters, letters, numbers, mixed case and 1 special character'
            ),
        rememberMe: z.boolean().default(true).optional()
    })

    const form = useForm({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            email: '',
            password: '',
            rememberMe: true
        }
    })

    const login = useAuthHelper(AuthHelpers.LoginRegisteredUserB2C)

    const {data: baskets} = useCustomerBaskets(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer, keepPreviousData: true}
    )
    const mergeBasket = useShopperBasketsMutation('mergeBasket')
    const hasBasketItem = baskets?.baskets?.[0]?.productItems
        ? baskets.baskets[0].productItems.length > 0
        : false
    const updateProfile = useUpdateProfile()

    const onSubmit = async (data: {email: string; password: string; rememberMe: boolean}) => {
        setShowAlert(false)

        try {
            await login.mutateAsync({
                username: data.email,
                password: data.password
            })

            // we only want to merge basket when the user is logged in as a recurring user
            // only recurring users trigger the login mutation, new user triggers register mutation
            // this logic needs to stay in this block because this is the only place that tells if a user is a recurring user
            // if you change logic here, also change it in login page
            const shouldMergeBasket = hasBasketItem && prevAuthType === 'guest'
            if (shouldMergeBasket) {
                mergeBasket.mutate({
                    headers: {
                        // This is not required since the request has no body
                        // but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                        'Content-Type': 'application/json'
                    },
                    parameters: {
                        createDestinationBasket: true
                    }
                })
            }

            updateProfile.mutate({
                trigger: 'login' as const,
                smsOptIn: false,
                loyaltyOptIn: false,
                tandc: false,
                deadline: '',
                phone: ''
            })

            closeDrawer()
        } catch (error) {
            setShowAlert(true)
            console.log('Login error: ', error)
        }
    }

    const {url: determinedUrl, routeType: determinedRouteType} = useDetermineRouteTypeAndUrl({
        clickFunction: buttonFunctions.linkToPage,
        isInternalLink: true,
        pageReference: checkoutPageReference
    })

    const fallbackRouteType = routeTypes?.SFRA || 'SFRA'
    const fallbackUrl = '/checkout?stage=shipping#shipping'
    const routeType = determinedRouteType || fallbackRouteType
    const url = determinedUrl || fallbackUrl
    const {email, password, rememberMe, forgotPassword} = formLabels || {}
    const signInCTA = 'Sign In'

    const handleTurnstileSuccess = useCallback((token: string) => {
        setIsButtonDisabled(false)
        // Handle token if needed
        console.log('Turnstile token:', token)
    }, [])

    return (
        <Form {...form} data-testid="signInForm">
            <AccountErrorSummary
                errors={form.formState.errors}
                fieldLabels={{password: 'Password'}}
                data-testid={'signInForm-AccountErrorSummary'}
            />
            <TurnstileWidget
                data-testid="signInForm-TurnstileWidget"
                onSuccess={handleTurnstileSuccess}
            />
            <form
                {...remainingProps}
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn(className, 'space-y-6')}
            >
                {showAlert && (
                    <Alert variant="destructive" data-testid="signInForm-Alert">
                        <AlertDescription data-testid="signInForm-AlertDescription">
                            Invalid login or password. Remember that password is case-sensitive.
                            Please try again.
                        </AlertDescription>
                    </Alert>
                )}

                <FormField
                    control={form.control}
                    name="email"
                    render={({field}) => (
                        <FormInput
                            label={email || ''}
                            inputType="email"
                            fieldProps={field}
                            required={true}
                            validate={() => form.trigger('email')}
                            data-testid="signInForm-FormInput"
                        />
                    )}
                    data-testid="signInForm-FormField"
                />
                <FormField
                    control={form.control}
                    name="password"
                    render={({field}) => (
                        <FormInput
                            label={password || ''}
                            inputType="password"
                            fieldProps={field}
                            required={true}
                            validate={() => form.trigger('password')}
                            data-testid="signInForm-FormInput"
                        />
                    )}
                    data-testid="signInForm-FormField"
                />
                <div className="flex justify-between items-center">
                    <FormField
                        control={form.control}
                        name="rememberMe"
                        render={({field}) => (
                            <FormCheckbox
                                label={rememberMe || ''}
                                fieldProps={field}
                                data-testid="signInForm-FormCheckbox"
                            />
                        )}
                        data-testid="signInForm-FormField"
                    />
                    <PrimativeButton
                        type="button"
                        ctaType="button"
                        variant="normal"
                        className="body-md lg:text-sm underline"
                        onClick={onForgotPassword}
                        data-testid="signInForm-Button"
                        disabled={isButtonDisabled}
                    >
                        {forgotPassword}
                    </PrimativeButton>
                </div>
                <div className="flex flex-col gap-3 items-center">
                    <PrimativeButton
                        disabled={turnstileEnabled && isButtonDisabled}
                        type="submit"
                        ctaType="button"
                        variant="primary"
                        className="w-[18.75rem] mx-auto"
                        data-testid="signInForm-Button"
                    >
                        {signInCTA}
                    </PrimativeButton>
                    {basketTotalItems > 0 && !isLoggedIn && enableGuestCheckout ? (
                        <Button
                            disabled={turnstileEnabled && isButtonDisabled}
                            type="button"
                            ctaType="button"
                            variant="navLink"
                            className="w-[18.75rem] mx-auto underline font-grotesque text-center font-medium text-lg cursor-pointer hover:text-blue-04 hover:bg-transparent hover:underline disabled:text-gray-400 disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                            data-testid="signInForm-GuestCheckoutButton"
                            clickFunction={'Link to Page'}
                            routeType={routeType}
                            url={url}
                        >
                            Continue as guest
                        </Button>
                    ) : null}
                </div>
                {googleOAuthEnabled && (
                    <div className="w-[18.75rem] mx-auto">
                        <GoogleSignIn data-testid="signInForm-GoogleSignIn" />
                    </div>
                )}
            </form>
        </Form>
    )
}

export default SignInForm
